import React, { useEffect, useRef, useState } from 'react';
import proj4 from 'proj4';
import apiConfig from '../config/api';
import './LocationMap.css';

// Define projections
// ITM (Israel Transverse Mercator) - EPSG:2039
proj4.defs("EPSG:2039", "+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs");
// WGS84 (Google Maps) - EPSG:4326
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
// Web Mercator (Google Maps internal / some GIS) - EPSG:3857
proj4.defs("EPSG:3857", "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs");

const mapPalette = {
  geometry: '#eef2f3',
  text: '#1f2a37',
  labelStroke: '#ffffff',
  poi: '#4b63d3',
  park: '#79c8ad',
  road: '#ffffff',
  roadStroke: '#b7c5cf',
  highway: '#4b63d3',
  highwayStroke: '#263d74',
  highwayLabel: '#fcfcfc',
  transit: '#6fbca6',
  water: '#8fcfbe',
  markerBg: '#f54c60',
  markerBorder: '#263d74',
  markerGlyph: '#ffffff',
  polygonStroke: '#263d74',
  polygonFill: '#7e95ff',
};

const stripHash = (value) => String(value || '').replace(/^#/, '').toLowerCase();

// Map styles matching the active site theme.
const mapStyles = [
  {
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.geometry }]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.text }]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [{ "color": mapPalette.labelStroke }]
  },
  {
    "featureType": "administrative.locality",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.text }]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.poi }]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.park }]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.road }]
  },
  {
    "featureType": "road",
    "elementType": "geometry.stroke",
    "stylers": [{ "color": mapPalette.roadStroke }]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.highway }]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [{ "color": mapPalette.highwayStroke }]
  },
  {
    "featureType": "road.highway",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.highwayLabel }]
  },
  {
    "featureType": "transit",
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.transit }]
  },
  {
    "featureType": "transit.station",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.text }]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{ "color": mapPalette.water }]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": mapPalette.text }]
  }
];

// Google Static Maps style directives to match the dynamic map theme above.
const staticMapStyles = [
  `element:geometry|color:0x${stripHash(mapPalette.geometry)}`,
  `element:labels.text.fill|color:0x${stripHash(mapPalette.text)}`,
  `element:labels.text.stroke|color:0x${stripHash(mapPalette.labelStroke)}`,
  `feature:administrative.locality|element:labels.text.fill|color:0x${stripHash(mapPalette.text)}`,
  `feature:poi|element:labels.text.fill|color:0x${stripHash(mapPalette.poi)}`,
  `feature:poi.park|element:geometry|color:0x${stripHash(mapPalette.park)}`,
  `feature:road|element:geometry|color:0x${stripHash(mapPalette.road)}`,
  `feature:road|element:geometry.stroke|color:0x${stripHash(mapPalette.roadStroke)}`,
  `feature:road.highway|element:geometry|color:0x${stripHash(mapPalette.highway)}`,
  `feature:road.highway|element:geometry.stroke|color:0x${stripHash(mapPalette.highwayStroke)}`,
  `feature:road.highway|element:labels.text.fill|color:0x${stripHash(mapPalette.highwayLabel)}`,
  `feature:transit|element:geometry|color:0x${stripHash(mapPalette.transit)}`,
  `feature:transit.station|element:labels.text.fill|color:0x${stripHash(mapPalette.text)}`,
  `feature:water|element:geometry|color:0x${stripHash(mapPalette.water)}`,
  `feature:water|element:labels.text.fill|color:0x${stripHash(mapPalette.text)}`,
];

const detectWkidFromPoint = (x, y) => {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return 4326;
  if (Math.abs(x) > 1_000_000 || Math.abs(y) > 1_000_000) return 3857;
  if (x > 100_000 && x < 300_000 && y > 300_000 && y < 1_000_000) return 2039;
  return null;
};

const isIsraelLatLng = (pt) => {
  if (!pt) return false;
  return pt.lat >= 29 && pt.lat <= 34 && pt.lng >= 34 && pt.lng <= 36.5;
};

function toLatLng(x, y, wkidHint) {
  const wkidRaw = Number(wkidHint || 0);
  const detected = detectWkidFromPoint(x, y);
  const wkid = [2039, 3857, 4326].includes(wkidRaw) ? wkidRaw : detected;

  if (wkid === 4326) {
    const pt = { lat: y, lng: x };
    return isIsraelLatLng(pt) ? pt : null;
  }
  try {
    if (wkid === 2039) {
      const result = proj4('EPSG:2039', 'EPSG:4326', [x, y]);
      const pt = { lat: result[1], lng: result[0] };
      return isIsraelLatLng(pt) ? pt : null;
    }
    if (wkid === 3857) {
      const result = proj4('EPSG:3857', 'EPSG:4326', [x, y]);
      const pt = { lat: result[1], lng: result[0] };
      return isIsraelLatLng(pt) ? pt : null;
    }
  } catch (err) {
    console.warn('Geometry conversion failed:', err?.message || err);
  }
  return null;
}

function coordinatesToLatLng(coordinates) {
  if (!coordinates) return null;
  if (typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number') {
    return isIsraelLatLng(coordinates) ? coordinates : null;
  }
  if (typeof coordinates.x === 'number' && typeof coordinates.y === 'number') {
    const { x, y } = coordinates;
    if (x > 3000000 && y > 3000000) {
      const result = proj4('EPSG:3857', 'EPSG:4326', [x, y]);
      const pt = { lng: result[0], lat: result[1] };
      return isIsraelLatLng(pt) ? pt : null;
    }
    if (x > 100000 && x < 300000 && y > 300000 && y < 1000000) {
      const result = proj4('EPSG:2039', 'EPSG:4326', [x, y]);
      const pt = { lng: result[0], lat: result[1] };
      return isIsraelLatLng(pt) ? pt : null;
    }
    if (y >= 29 && y <= 34 && x >= 34 && x <= 36.5) {
      const pt = { lat: y, lng: x };
      return isIsraelLatLng(pt) ? pt : null;
    }
  }
  return null;
}

function downsamplePoints(points, maxPoints = 80) {
  if (!Array.isArray(points) || points.length <= maxPoints) return points || [];
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, idx) => idx % step === 0);
}

function geometryPathParam(geometry) {
  if (!geometry || typeof geometry !== 'object') return null;
  const wkid = geometry.spatialReference?.latestWkid || geometry.spatialReference?.wkid || null;

  if (Array.isArray(geometry.rings) && geometry.rings.length) {
    const outer = geometry.rings[0] || [];
    const points = downsamplePoints(
      outer
        .map((point) => (Array.isArray(point) && point.length >= 2 ? toLatLng(Number(point[0]), Number(point[1]), wkid) : null))
        .filter(Boolean)
    );
    if (points.length >= 3) {
      const coords = points.map((p) => `${p.lat},${p.lng}`).join('|');
      return `color:0x${stripHash(mapPalette.polygonStroke)}ff|weight:2|fillcolor:0x${stripHash(mapPalette.polygonFill)}44|${coords}`;
    }
  }

  if (Array.isArray(geometry.paths) && geometry.paths.length) {
    const line = geometry.paths[0] || [];
    const points = downsamplePoints(
      line
        .map((point) => (Array.isArray(point) && point.length >= 2 ? toLatLng(Number(point[0]), Number(point[1]), wkid) : null))
        .filter(Boolean)
    );
    if (points.length >= 2) {
      const coords = points.map((p) => `${p.lat},${p.lng}`).join('|');
      return `color:0x${stripHash(mapPalette.polygonStroke)}ff|weight:3|${coords}`;
    }
  }

  return null;
}

function geometryBoundsLatLng(geometry) {
  if (!geometry || typeof geometry !== 'object') return null;
  const wkid = geometry.spatialReference?.latestWkid || geometry.spatialReference?.wkid || null;
  const points = [];

  if (Array.isArray(geometry.rings)) {
    geometry.rings.forEach((ring) => {
      (ring || []).forEach((point) => {
        if (Array.isArray(point) && point.length >= 2) {
          const p = toLatLng(Number(point[0]), Number(point[1]), wkid);
          if (p) points.push(p);
        }
      });
    });
  } else if (Array.isArray(geometry.paths)) {
    geometry.paths.forEach((path) => {
      (path || []).forEach((point) => {
        if (Array.isArray(point) && point.length >= 2) {
          const p = toLatLng(Number(point[0]), Number(point[1]), wkid);
          if (p) points.push(p);
        }
      });
    });
  } else if (geometry.x !== undefined && geometry.y !== undefined) {
    const p = toLatLng(Number(geometry.x), Number(geometry.y), wkid);
    if (p) points.push(p);
  }

  if (!points.length) return null;
  const minLat = Math.min(...points.map((p) => p.lat));
  const maxLat = Math.max(...points.map((p) => p.lat));
  const minLng = Math.min(...points.map((p) => p.lng));
  const maxLng = Math.max(...points.map((p) => p.lng));
  return { minLat, maxLat, minLng, maxLng };
}

function zoomForBounds(bounds, widthPx = 640, heightPx = 320) {
  if (!bounds) return null;

  const latDiff = Math.max(Math.abs(bounds.maxLat - bounds.minLat), 1e-6);
  const lngDiff = Math.max(Math.abs(bounds.maxLng - bounds.minLng), 1e-6);

  const latZoom = Math.log2((heightPx * 170) / (latDiff * 256));
  const lngZoom = Math.log2((widthPx * 360) / (lngDiff * 256));
  const base = Math.floor(Math.min(latZoom, lngZoom));

  // Slight zoom-in bias so full-boundary framing doesn't feel too distant.
  return Math.max(8, Math.min(20, base + 2));
}

function applyZoomDelta(zoom, delta = 0) {
  const z = Number.isFinite(zoom) ? zoom : 16;
  const d = Number.isFinite(delta) ? delta : 0;
  return Math.max(1, Math.min(20, z + d));
}

const LocationMap = ({ coordinates, address, stacked = false, showStreetView = true, geometry = null, showTitles = true, flat = false, useStatic = true, zoomDelta = 0 }) => {
  const mapRef = useRef(null);
  const streetViewRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const center = coordinatesToLatLng(coordinates);
  const mapsQuery = center
    ? `${center.lat},${center.lng}`
    : (address || '').trim();
  const openInGoogleMapsUrl = mapsQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`
    : null;
  const openInGoogleStreetViewUrl = center
    ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${encodeURIComponent(`${center.lat},${center.lng}`)}`
    : null;
  const staticPath = geometryPathParam(geometry);
  const staticBounds = geometryBoundsLatLng(geometry);
  const staticBoundaryZoom = zoomForBounds(staticBounds);
  const staticMapUrl = center
    ? (() => {
        const params = new URLSearchParams({
          type: 'map',
          size: '640x320',
          scale: '2',
          maptype: 'roadmap',
        });
        staticMapStyles.forEach((style) => params.append('style', style));
        if (staticPath) {
          const fitCenter =
            staticBounds
              ? {
                  lat: (staticBounds.minLat + staticBounds.maxLat) / 2,
                  lng: (staticBounds.minLng + staticBounds.maxLng) / 2,
                }
              : center;
          if (fitCenter) params.set('center', `${fitCenter.lat},${fitCenter.lng}`);
          if (staticBoundaryZoom) params.set('zoom', String(applyZoomDelta(staticBoundaryZoom, zoomDelta)));
          params.append('path', staticPath);
        } else {
          params.set('center', `${center.lat},${center.lng}`);
          params.set('zoom', String(applyZoomDelta(16, zoomDelta)));
          params.append('markers', `color:0x${stripHash(mapPalette.markerBg)}|${center.lat},${center.lng}`);
        }
        return `${apiConfig.apiBaseUrl}/api/google-static?${params.toString()}`;
      })()
    : null;
  const staticStreetViewUrl = center && showStreetView
    ? (() => {
        const params = new URLSearchParams({
          type: 'streetview',
          size: '640x320',
          location: `${center.lat},${center.lng}`,
          fov: '90',
          pitch: '10',
          source: 'outdoor',
          radius: '60',
          autoheading: '1',
        });
        return `${apiConfig.apiBaseUrl}/api/google-static?${params.toString()}`;
      })()
    : null;

  useEffect(() => {
    if (useStatic) return;
    // Only initialize if we have valid coordinates and google maps is loaded
    if (!coordinates || !window.google || !window.google.maps) {
      return;
    }

    let isCancelled = false;

    const initializeMap = async () => {
      let lat, lng;

      try {
        if (typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number') {
          // Already WGS84
          lat = coordinates.lat;
          lng = coordinates.lng;
        } else if (typeof coordinates.x === 'number' && typeof coordinates.y === 'number') {
            // Need to detect coordinate system
            const { x, y } = coordinates;
            
            // Check for EPSG:3857 (Web Mercator) - Values in millions (approx 3,000,000 for Israel)
            if (x > 3000000 && y > 3000000) {
               console.log('🗺️ Detected Web Mercator (EPSG:3857) coordinates, converting to WGS84...');
               const result = proj4('EPSG:3857', 'EPSG:4326', [x, y]);
               lng = result[0];
               lat = result[1];
            } 
            // Check for ITM (EPSG:2039) - X approx 100,000-300,000, Y approx 300,000-800,000
            else if (x > 100000 && x < 300000 && y > 300000 && y < 1000000) {
               console.log('🗺️ Detected ITM (EPSG:2039) coordinates, converting to WGS84...');
               const result = proj4('EPSG:2039', 'EPSG:4326', [x, y]);
               lng = result[0];
               lat = result[1];
            }
            // Maybe it's already lat/lng in x/y fields?
            else if (y >= 29 && y <= 34 && x >= 34 && x <= 36) {
               lat = coordinates.y;
               lng = coordinates.x;
            } else {
                console.error('❌ LocationMap received unknown coordinate system:', coordinates);
                setMapError('לא ניתן להציג מפה עבור קואורדינטות אלו');
                return;
            }
        } else {
          console.error('❌ LocationMap received invalid coordinates format:', coordinates);
          setMapError('נתוני מיקום לא תקינים');
          return;
        }

        // Validate calculated coordinates
        if (isNaN(lat) || isNaN(lng)) {
            console.error('❌ LocationMap failed to convert coordinates:', { original: coordinates, converted: { lat, lng } });
            setMapError('שגיאה בהמרת קואורדינטות');
            return;
        }

        const position = { lat, lng };
        console.log('🗺️ Initializing map with position:', position);

        // Initialize Map with static settings and custom styles
        const map = new window.google.maps.Map(mapRef.current, {
          center: position,
          zoom: applyZoomDelta(17, zoomDelta),
          // Disable interactions for static view
          disableDefaultUI: true,
          draggable: false,
          zoomControl: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          clickableIcons: false,
          keyboardShortcuts: false,
          // Apply custom styles
          styles: mapStyles
        });

        let geometryDrawn = false;
        const g = geometry;
        if (g && typeof g === 'object') {
          const wkid = g.spatialReference?.latestWkid || g.spatialReference?.wkid || null;
          const bounds = new window.google.maps.LatLngBounds();

          if (Array.isArray(g.rings) && g.rings.length > 0) {
            const paths = g.rings.map((ring) =>
              ring
                .map((point) => {
                  if (!Array.isArray(point) || point.length < 2) return null;
                  return toLatLng(Number(point[0]), Number(point[1]), wkid);
                })
                .filter(Boolean)
            ).filter((ring) => ring.length > 2);

            if (paths.length) {
              paths.forEach((ring) => ring.forEach((pt) => bounds.extend(pt)));
              new window.google.maps.Polygon({
                map,
                paths,
                strokeColor: mapPalette.polygonStroke,
                strokeOpacity: 0.95,
                strokeWeight: 2,
                fillColor: mapPalette.polygonFill,
                fillOpacity: 0.22,
              });
              const center = bounds.getCenter();
              if (isIsraelLatLng({ lat: center.lat(), lng: center.lng() })) {
                map.fitBounds(bounds, 36);
                geometryDrawn = true;
              }
            }
          } else if (Array.isArray(g.paths) && g.paths.length > 0) {
            const paths = g.paths.map((line) =>
              line
                .map((point) => {
                  if (!Array.isArray(point) || point.length < 2) return null;
                  return toLatLng(Number(point[0]), Number(point[1]), wkid);
                })
                .filter(Boolean)
            ).filter((line) => line.length > 1);

            if (paths.length) {
              paths.forEach((line) => line.forEach((pt) => bounds.extend(pt)));
              paths.forEach((path) => {
                new window.google.maps.Polyline({
                  map,
                  path,
                  strokeColor: mapPalette.polygonStroke,
                  strokeOpacity: 0.95,
                  strokeWeight: 3,
                });
              });
              const center = bounds.getCenter();
              if (isIsraelLatLng({ lat: center.lat(), lng: center.lng() })) {
                map.fitBounds(bounds, 36);
                geometryDrawn = true;
              }
            }
          }
        }

        // Add marker when geometry isn't usable
        if (!geometryDrawn) {
          if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
            const pinBackground = new window.google.maps.marker.PinElement({
              background: mapPalette.markerBg,
              borderColor: mapPalette.markerBorder,
              glyphColor: mapPalette.markerGlyph,
            });
            new window.google.maps.marker.AdvancedMarkerElement({
              map,
              position,
              title: address,
              content: pinBackground.element,
            });
          } else {
            new window.google.maps.Marker({
              position: position,
              map: map,
              title: address,
            });
          }
        }

        // Try to snap Street View to nearest outdoor panorama to avoid indoor panos
        const snapToOutdoorPanorama = () => {
          return new Promise(resolve => {
            if (!window.google.maps.StreetViewService) {
              resolve(null);
              return;
            }
            const svService = new window.google.maps.StreetViewService();
            const request = {
              location: position,
              radius: 50, // meters
              source: window.google.maps.StreetViewSource ? window.google.maps.StreetViewSource.OUTDOOR : undefined,
            };
            svService.getPanorama(request, (data, status) => {
              if (status === window.google.maps.StreetViewStatus.OK && data?.location?.latLng) {
                resolve({
                  panoId: data.location.pano,
                  latLng: {
                    lat: data.location.latLng.lat(),
                    lng: data.location.latLng.lng(),
                  }
                });
              } else {
                resolve(null);
              }
            });
          });
        };

        if (showStreetView) {
          const panoTarget = await snapToOutdoorPanorama();
          if (isCancelled) return;

          // Initialize Street View - keep interactive but minimal UI.
          const streetViewOptions = {
            position: panoTarget?.latLng || position,
            pano: panoTarget?.panoId,
            pov: {
              heading: 34,
              pitch: 10,
            },
            zoom: 1,
            addressControl: false,
            fullscreenControl: true,
            motionTracking: false,
            motionTrackingControl: false,
            showRoadLabels: false,
          };

          new window.google.maps.StreetViewPanorama(streetViewRef.current, streetViewOptions);
        }

        // Don't link map to street view if map is static (avoid pegman moving the static map)
        // map.setStreetView(streetView); 

      } catch (error) {
        if (!isCancelled) {
          console.error('Error initializing map:', error);
          setMapError('לא ניתן לטעון את המפה כרגע');
        }
      }
    };

    initializeMap();

    return () => {
      isCancelled = true;
    };
  }, [coordinates, address, showStreetView, geometry, useStatic, zoomDelta]);

  if (!coordinates) {
    return null;
  }

  return (
    <div className={`location-map-container ${stacked ? 'stacked' : ''} ${flat ? 'flat' : ''}`}>
      <div className="map-section">
        {showTitles ? <h3 className="map-title"><i className="fi fi-rr-map-marker"></i> מפה</h3> : null}
        {useStatic ? (
          staticMapUrl ? (
            <img
              src={staticMapUrl}
              alt={address || 'Map'}
              className="google-map"
              loading="lazy"
              onError={() => setMapError('לא ניתן לטעון את המפה כרגע')}
            />
          ) : (
            <div className="google-map static-map-fallback">No map preview available</div>
          )
        ) : (
          <div ref={mapRef} className="google-map" />
        )}
        {openInGoogleMapsUrl ? (
          <a
            href={openInGoogleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="map-external-link"
            aria-label="פתיחה ב-Google Maps"
          >
            Google Maps
          </a>
        ) : null}
      </div>
      {showStreetView ? (
        <div className="street-view-section">
          {showTitles ? <h3 className="map-title"><i className="fi fi-rr-street-view"></i> מבט רחוב</h3> : null}
          {useStatic ? (
            staticStreetViewUrl ? (
              <img
                src={staticStreetViewUrl}
                alt={`Street view ${address || ''}`.trim()}
                className="street-view"
                loading="lazy"
                onError={() => setMapError('לא ניתן לטעון תצוגת רחוב כרגע')}
              />
            ) : (
              <div className="street-view static-map-fallback">No street view preview available</div>
            )
          ) : (
            <div ref={streetViewRef} className="street-view" />
          )}
          {openInGoogleStreetViewUrl ? (
            <a
              href={openInGoogleStreetViewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="map-external-link"
              aria-label="פתיחה ב-Google Street View"
            >
              Google Street View
            </a>
          ) : null}
        </div>
      ) : null}
      {mapError && <div className="map-error">{mapError}</div>}
    </div>
  );
};

export default LocationMap;
